import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from './contexts/AuthContext';
import { queryClient } from './lib/queryClient';
import Dashboard from './Dashboard';
import Login from './components/Login';
import Signup from './components/Signup';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
// AcceptInvite removed - Brightstone internal use only
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';
import Meetings from './pages/Meetings';
// Alerts page removed - using pulses instead
// import Alerts from './pages/Alerts';
import SharedAlert from './pages/SharedAlert';
import SharePulse from './pages/SharePulse';
import Settings from './pages/Settings';
import LayoutExamples from './pages/LayoutExamples';
import ProvincialTracking from './pages/ProvincialTracking';
import LegalTracking from './pages/LegalTracking';
import MayoralDecisions from './pages/MayoralDecisions';
import ReportProblem from './pages/ReportProblem';
import Records from './pages/Records';
import DevelopmentMap from './pages/DevelopmentMap';
import Sources from './pages/Sources';
import Projects from './pages/Projects';
import Acquisitions from './pages/Acquisitions';
import Municipalities from './pages/Municipalities';
import LTB from './pages/LTB';
import AddProperty from './pages/add-property';
// Admin pages removed - Brightstone internal use only
import { Toaster } from './components/ui/toaster';
import './App.css';

const App: React.FC = () => {
  // App component mounted
  
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <Toaster />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          {/* Accept invite route removed - Brightstone internal use only */}
          {/* Public route for shared alerts - no authentication required */}
          <Route path="/shared-alert/:token" element={<SharedAlert />} />
          {/* Public route for shared pulses - secure token-based */}
          <Route path="/s/:code" element={<SharePulse />} />
          <Route path="/" element={
            <PrivateRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </PrivateRoute>
          } />
          <Route path="/meetings" element={
            <PrivateRoute>
              <Layout>
                <Meetings />
              </Layout>
            </PrivateRoute>
          } />
          <Route path="/projects" element={
            <PrivateRoute>
              <Layout>
                <Projects />
              </Layout>
            </PrivateRoute>
          } />
          <Route path="/acquisitions" element={
            <PrivateRoute>
              <Layout>
                <Acquisitions />
              </Layout>
            </PrivateRoute>
          } />
          {/* Alerts route removed - using pulses instead */}
          {/* <Route path="/alerts" element={
            <PrivateRoute>
              <Layout>
                <Alerts />
              </Layout>
            </PrivateRoute>
          } /> */}
          <Route path="/provincial" element={
            <PrivateRoute>
              <Layout>
                <ProvincialTracking />
              </Layout>
            </PrivateRoute>
          } />
          <Route path="/precedents" element={
            <PrivateRoute>
              <Layout>
                <LegalTracking />
              </Layout>
            </PrivateRoute>
          } />
          <Route path="/decisions" element={
            <PrivateRoute>
              <Layout>
                <MayoralDecisions />
              </Layout>
            </PrivateRoute>
          } />
          <Route path="/records" element={
            <PrivateRoute>
              <Layout>
                <Records />
              </Layout>
            </PrivateRoute>
          } />
          <Route path="/map" element={
            <PrivateRoute>
              <Layout>
                <DevelopmentMap />
              </Layout>
            </PrivateRoute>
          } />
          <Route path="/development-map" element={
            <PrivateRoute>
              <Layout>
                <DevelopmentMap />
              </Layout>
            </PrivateRoute>
          } />
          <Route path="/sources" element={
            <PrivateRoute>
              <Layout>
                <Sources />
              </Layout>
            </PrivateRoute>
          } />
          <Route path="/municipalities" element={
            <PrivateRoute>
              <Layout>
                <Municipalities />
              </Layout>
            </PrivateRoute>
          } />
          <Route path="/ltb" element={
            <PrivateRoute>
              <Layout>
                <LTB />
              </Layout>
            </PrivateRoute>
          } />
          <Route path="/properties/new" element={
            <PrivateRoute>
              <AddProperty />
            </PrivateRoute>
          } />
          <Route path="/add-property" element={
            <PrivateRoute>
              <AddProperty />
            </PrivateRoute>
          } />
          <Route path="/settings" element={
            <PrivateRoute>
              <Layout>
                <Settings />
              </Layout>
            </PrivateRoute>
          } />
          <Route path="/settings/account" element={
            <PrivateRoute>
              <Layout>
                <Settings />
              </Layout>
            </PrivateRoute>
          } />
          {/* Settings users/billing routes removed - Brightstone internal use only */}
          <Route path="/support" element={
            <PrivateRoute>
              <Layout>
                <ReportProblem />
              </Layout>
            </PrivateRoute>
          } />
          {/* Back-compat: old route redirects to /support */}
          <Route path="/report-problem" element={<Navigate to="/support" replace />} />
          <Route path="/layout-examples" element={
            <PrivateRoute>
              <Layout>
                <LayoutExamples />
              </Layout>
            </PrivateRoute>
          } />
          <Route path="/meetings-demo" element={
            <PrivateRoute>
              <Layout>
                <Meetings />
              </Layout>
            </PrivateRoute>
          } />
          {/* Admin routes removed - Brightstone internal use only */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
    {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
};

export default App;
